import { useMemo } from 'react';
import {
  Badge,
  Box,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Link,
  Text,
} from '@optiaxiom/react';

import { CompanionRuleExecution } from '@frontend/types/app-companion';
import { getExecutionLogsUrl, getExecutionResponseText } from '@frontend/utils/app-companion';

type RuleExecutionCardProps = {
  execution: CompanionRuleExecution;
  defaultOpen?: boolean;
};

export function RuleExecutionCard({ execution, defaultOpen = false }: RuleExecutionCardProps) {
  const responseText = useMemo(() => getExecutionResponseText(execution), [execution]);
  const executionLogsUrl = useMemo(() => getExecutionLogsUrl(execution), [execution]);

  return (
    <Disclosure defaultOpen={defaultOpen}>
      <DisclosureTrigger>
        <Field
          label={
            <Flex alignItems="center" gap="8" flexDirection="row">
              <Text fontSize="sm" fontWeight="500">
                Agent Execution
              </Text>
              <Badge
                variant="subtle"
                color={
                  execution.status === 'COMPLETED'
                    ? 'fg.success'
                    : execution.status === 'FAILED'
                      ? 'fg.error'
                      : 'fg.accent'
                }
              >
                {execution.status}
              </Badge>
            </Flex>
          }
        />
      </DisclosureTrigger>
      <DisclosureContent>
        <Flex flexDirection="column" gap="16" px="16">
          <Field label="Execution Details">
            <Box border="1" borderColor="border.default" rounded="md">
              <Box bg="bg.default" p="12">
                <Text fontSize="sm" fontFamily="mono">
                  ID: {execution.id}
                </Text>
                <Text fontSize="sm">
                  Started: {new Date(execution.created_at).toLocaleString()}
                </Text>
                {execution.completed_at && (
                  <Text fontSize="sm">
                    Completed: {new Date(execution.completed_at).toLocaleString()}
                  </Text>
                )}
                {executionLogsUrl && (
                  <Box mt="8">
                    <Link href={executionLogsUrl} target="_blank" rel="noopener noreferrer">
                      View execution logs
                    </Link>
                  </Box>
                )}
              </Box>
            </Box>
          </Field>

          {/* Agent Response */}
          <Field label="Agent Response">
            <Box border="1" borderColor="border.success" rounded="md">
              <Box bg="bg.success.subtle" p="12">
                <Text fontSize="sm" color="fg.default">
                  {responseText}
                </Text>
              </Box>
            </Box>
          </Field>
        </Flex>
      </DisclosureContent>
    </Disclosure>
  );
}
